import PropTypes from "prop-types";
import * as React from "react";
import { Link } from "react-router-dom";
import { mutate } from "swr";
import { Influencer } from "../../../domain";
import { cardSourceFromRow, shopCardText } from "../../../domain/shopImages";
import fetcher from "../../../hooks/fetcher";
import ShopCardImage from "../../ShopCardImage/ShopCardImage";
import * as constants from "../constants";
import LikeButton from "./LikeButton";
import UpcomingEvent from "./UpcomingEvent";
import css from "./ShopCard.module.css";

const usePrefetch = (endpoints) =>
  React.useCallback(() => {
    if (!endpoints) return;
    const urls = Array.isArray(endpoints) ? endpoints : [endpoints];
    urls.filter(Boolean).forEach((url) => mutate(url, fetcher(url)));
  }, [endpoints]);

export default function ShopCard({
  data: influencer,
  isExpanded,
  isMobile,
  look = constants.LOOK_TALL,
  style,
  recommendedShops,
  updateFavouriteStatus,
}) {
  const [isFavourite, setIsFavourite] = React.useState(false);
  const [showFavourite, setShowFavourite] = React.useState(isMobile);
  const hasFavourite = !isMobile && typeof updateFavouriteStatus === "function";

  React.useEffect(() => {
    setIsFavourite(Boolean(influencer.isFavourite));
  }, [influencer.isFavourite]);

  const onClick = React.useCallback(
    (evt) => {
      evt.stopPropagation();
      evt.preventDefault();

      updateFavouriteStatus?.(
        influencer.mutate("is_favourite", !isFavourite),
        (newStatus) => {
          setIsFavourite(newStatus);
        },
      );
    },
    [isFavourite, influencer, updateFavouriteStatus],
  );

  const prefetch = usePrefetch(
    influencer.endpoints && [
      influencer?.endpoints?.influencerByRoute,
      influencer?.endpoints?.influencerEvents,
    ],
  );

  const source = cardSourceFromRow(influencer);
  const text = shopCardText({
    source,
    name: influencer.name,
    famousFor: influencer.famousFor,
    famousCharacter: influencer.famousCharacter,
  });
  const imageWidth = constants.imageWidthFor({ look, isMobile });

  const linkClassName = [
    css.cardLink,
    !isExpanded && css.cardLinkAbsolute,
    isExpanded && css.cardLinkExpanded,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      style={style}
      onMouseOver={hasFavourite ? () => setShowFavourite(true) : undefined}
      onMouseLeave={hasFavourite ? () => setShowFavourite(false) : undefined}
    >
      <Link
        className={linkClassName}
        to={`/${influencer.route}${
          recommendedShops ? "?source=recommended" : ""
        }`}
        onMouseEnter={prefetch}
      >
        {influencer?.isLive && <span className={css.liveIndicator}>LIVE</span>}

        {!influencer?.isLive && <UpcomingEvent influencer={influencer} />}

        {hasFavourite && (isFavourite || showFavourite) && (
          <LikeButton
            isMobile={isMobile}
            onClick={onClick}
            isFavourite={isFavourite}
          />
        )}

        <div className={css.imageContainer}>
          <ShopCardImage
            className={css.image}
            style={{ inlineSize: imageWidth }}
            source={source}
            name={influencer.name}
            width={imageWidth}
            height={constants.imageHeightFor({ look, isMobile })}
            decorative={text.isCharacterTitle}
          />

          <p className={css.name}>{text.title}</p>
        </div>

        {constants.isTall(look) && <p className={css.text}>{text.subtitle}</p>}
      </Link>
    </div>
  );
}

ShopCard.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.instanceOf(Influencer),
    PropTypes.object,
  ]),
  isExpanded: PropTypes.bool,
  isMobile: PropTypes.bool,
  look: PropTypes.oneOf(["tall"]),
  style: PropTypes.object,
  updateFavouriteStatus: PropTypes.func,
  recommendedShops: PropTypes.bool,
};
